import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Badge } from '../../components/ui/Badge';
import { 
  Settings, 
  Shield, 
  Globe, 
  Bell, 
  Key, 
  Save,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Info,
  Lock,
  Users,
  Database,
  Smartphone
} from 'lucide-react';
import { useDataContext } from '../../contexts/DataContext';

const GlobalSettings = () => {
  const { getMockData, updateMockData } = useDataContext();
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  useEffect(() => {
    // Load global settings data
    const globalSettings = getMockData('globalSettings');
    setSettings(globalSettings);
  }, [getMockData]);

  const tabs = [
    { id: 'general', name: 'General', icon: Settings },
    { id: 'security', name: 'Security', icon: Shield },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'integrations', name: 'Integrations', icon: Globe },
    { id: 'licensing', name: 'Licensing', icon: Key }
  ];

  const handleSave = async () => {
    setIsSaving(true);
    setSaveStatus(null);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      updateMockData('globalSettings', settings);
      setSaveStatus('success');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    const defaultSettings = getMockData('globalSettings');
    setSettings(defaultSettings);
    setSaveStatus('reset');
    setTimeout(() => setSaveStatus(null), 3000);
  };

  const updateSetting = (category, key, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value
      }
    }));
  };

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Globe className="h-5 w-5 mr-2" />
            Platform Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="platformName">Platform Name</Label>
            <Input
              id="platformName"
              value={settings.general?.platformName || ''}
              onChange={(e) => updateSetting('general', 'platformName', e.target.value)}
              placeholder="HelpDesk Pro"
            />
          </div>
          <div>
            <Label htmlFor="defaultLanguage">Default Language</Label>
            <select
              id="defaultLanguage"
              value={settings.general?.defaultLanguage || 'en'}
              onChange={(e) => updateSetting('general', 'defaultLanguage', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="en">English</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
              <option value="de">German</option>
              <option value="zh">Chinese</option>
            </select>
          </div>
          <div>
            <Label htmlFor="timezone">Default Timezone</Label>
            <select
              id="timezone"
              value={settings.general?.timezone || 'UTC'}
              onChange={(e) => updateSetting('general', 'timezone', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="UTC">UTC</option>
              <option value="America/New_York">Eastern Time</option>
              <option value="America/Chicago">Central Time</option>
              <option value="America/Denver">Mountain Time</option>
              <option value="America/Los_Angeles">Pacific Time</option>
              <option value="Europe/London">London</option>
              <option value="Europe/Paris">Paris</option>
              <option value="Asia/Tokyo">Tokyo</option>
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="maintenanceMode"
              checked={settings.general?.maintenanceMode || false}
              onChange={(e) => updateSetting('general', 'maintenanceMode', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <Label htmlFor="maintenanceMode">Enable Maintenance Mode</Label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Users className="h-5 w-5 mr-2" />
            User Management
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="maxUsers">Maximum Users per Tenant</Label>
            <Input
              id="maxUsers"
              type="number"
              value={settings.general?.maxUsers || 1000}
              onChange={(e) => updateSetting('general', 'maxUsers', parseInt(e.target.value))}
            />
          </div>
          <div>
            <Label htmlFor="sessionTimeout">Session Timeout (minutes)</Label>
            <Input
              id="sessionTimeout"
              type="number"
              value={settings.general?.sessionTimeout || 30}
              onChange={(e) => updateSetting('general', 'sessionTimeout', parseInt(e.target.value))}
            />
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="allowSelfRegistration"
              checked={settings.general?.allowSelfRegistration || false}
              onChange={(e) => updateSetting('general', 'allowSelfRegistration', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <Label htmlFor="allowSelfRegistration">Allow Self Registration</Label>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Shield className="h-5 w-5 mr-2" />
            Authentication & Security
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="passwordMinLength">Minimum Password Length</Label>
            <Input
              id="passwordMinLength"
              type="number"
              value={settings.security?.passwordMinLength || 8}
              onChange={(e) => updateSetting('security', 'passwordMinLength', parseInt(e.target.value))}
            />
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="requireSpecialChars"
              checked={settings.security?.requireSpecialChars || false}
              onChange={(e) => updateSetting('security', 'requireSpecialChars', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <Label htmlFor="requireSpecialChars">Require Special Characters</Label>
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="requireNumbers"
              checked={settings.security?.requireNumbers || false}
              onChange={(e) => updateSetting('security', 'requireNumbers', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <Label htmlFor="requireNumbers">Require Numbers</Label>
          </div>
          <div>
            <Label htmlFor="maxLoginAttempts">Maximum Login Attempts</Label>
            <Input
              id="maxLoginAttempts"
              type="number"
              value={settings.security?.maxLoginAttempts || 5}
              onChange={(e) => updateSetting('security', 'maxLoginAttempts', parseInt(e.target.value))}
            />
          </div>
          <div>
            <Label htmlFor="lockoutDuration">Lockout Duration (minutes)</Label>
            <Input
              id="lockoutDuration"
              type="number"
              value={settings.security?.lockoutDuration || 15}
              onChange={(e) => updateSetting('security', 'lockoutDuration', parseInt(e.target.value))}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Lock className="h-5 w-5 mr-2" />
            Data Protection
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="enableEncryption"
              checked={settings.security?.enableEncryption || true}
              onChange={(e) => updateSetting('security', 'enableEncryption', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <Label htmlFor="enableEncryption">Enable Data Encryption</Label>
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="enableAuditLogs"
              checked={settings.security?.enableAuditLogs || true}
              onChange={(e) => updateSetting('security', 'enableAuditLogs', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <Label htmlFor="enableAuditLogs">Enable Audit Logs</Label>
          </div>
          <div>
            <Label htmlFor="dataRetentionDays">Data Retention Period (days)</Label>
            <Input
              id="dataRetentionDays"
              type="number"
              value={settings.security?.dataRetentionDays || 365}
              onChange={(e) => updateSetting('security', 'dataRetentionDays', parseInt(e.target.value))}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Bell className="h-5 w-5 mr-2" />
            Email Notifications
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="smtpHost">SMTP Host</Label>
            <Input
              id="smtpHost"
              value={settings.notifications?.smtpHost || ''}
              onChange={(e) => updateSetting('notifications', 'smtpHost', e.target.value)}
              placeholder="smtp.example.com"
            />
          </div>
          <div>
            <Label htmlFor="smtpPort">SMTP Port</Label>
            <Input
              id="smtpPort"
              type="number"
              value={settings.notifications?.smtpPort || 587}
              onChange={(e) => updateSetting('notifications', 'smtpPort', parseInt(e.target.value))}
            />
          </div>
          <div>
            <Label htmlFor="fromEmail">From Email Address</Label>
            <Input
              id="fromEmail"
              type="email"
              value={settings.notifications?.fromEmail || ''}
              onChange={(e) => updateSetting('notifications', 'fromEmail', e.target.value)}
              placeholder="noreply@helpdesk.com"
            />
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="enableEmailNotifications"
              checked={settings.notifications?.enableEmailNotifications || true}
              onChange={(e) => updateSetting('notifications', 'enableEmailNotifications', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <Label htmlFor="enableEmailNotifications">Enable Email Notifications</Label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Smartphone className="h-5 w-5 mr-2" />
            Push Notifications
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="enablePushNotifications"
              checked={settings.notifications?.enablePushNotifications || false}
              onChange={(e) => updateSetting('notifications', 'enablePushNotifications', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <Label htmlFor="enablePushNotifications">Enable Push Notifications</Label>
          </div>
          <div>
            <Label htmlFor="firebaseConfig">Firebase Configuration</Label>
            <textarea
              id="firebaseConfig"
              value={settings.notifications?.firebaseConfig || ''}
              onChange={(e) => updateSetting('notifications', 'firebaseConfig', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={4}
              placeholder="Firebase configuration JSON"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const toggleIntegration = (integrationKey) => {
    setSettings(prev => ({
      ...prev,
      integrations: {
        ...prev.integrations,
        [integrationKey]: {
          ...prev.integrations?.[integrationKey],
          enabled: !prev.integrations?.[integrationKey]?.enabled
        }
      }
    }));
  };

  const renderIntegrationSettings = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Globe className="h-5 w-5 mr-2" />
            Third-Party Integrations
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-semibold">LD</span>
              </div>
              <div>
                <h3 className="font-semibold">LDAP Integration</h3>
                <p className="text-sm text-gray-600">Active Directory authentication</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Badge variant={settings.integrations?.ldap?.enabled ? 'default' : 'secondary'}>
                {settings.integrations?.ldap?.enabled ? 'Enabled' : 'Disabled'}
              </Badge>
              <Button
                variant={settings.integrations?.ldap?.enabled ? 'outline' : 'default'}
                size="sm"
                onClick={() => toggleIntegration('ldap')}
              >
                {settings.integrations?.ldap?.enabled ? 'Disable' : 'Enable'}
              </Button>
            </div>
          </div>
          
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 font-semibold">SS</span>
              </div>
              <div>
                <h3 className="font-semibold">Single Sign-On (SSO)</h3>
                <p className="text-sm text-gray-600">SAML/OAuth integration</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Badge variant={settings.integrations?.sso?.enabled ? 'default' : 'secondary'}>
                {settings.integrations?.sso?.enabled ? 'Enabled' : 'Disabled'}
              </Badge>
              <Button
                variant={settings.integrations?.sso?.enabled ? 'outline' : 'default'}
                size="sm"
                onClick={() => toggleIntegration('sso')}
              >
                {settings.integrations?.sso?.enabled ? 'Disable' : 'Enable'}
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 font-semibold">API</span>
              </div>
              <div>
                <h3 className="font-semibold">REST API</h3>
                <p className="text-sm text-gray-600">External API access</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Badge variant={settings.integrations?.api?.enabled ? 'default' : 'secondary'}>
                {settings.integrations?.api?.enabled ? 'Enabled' : 'Disabled'}
              </Badge>
              <Button
                variant={settings.integrations?.api?.enabled ? 'outline' : 'default'}
                size="sm"
                onClick={() => toggleIntegration('api')}
              >
                {settings.integrations?.api?.enabled ? 'Disable' : 'Enable'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderLicensingSettings = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Key className="h-5 w-5 mr-2" />
            License Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="licenseKey">License Key</Label>
            <Input
              id="licenseKey"
              value={settings.licensing?.licenseKey || ''}
              onChange={(e) => updateSetting('licensing', 'licenseKey', e.target.value)}
              placeholder="Enter your license key"
            />
          </div>
          <div>
            <Label htmlFor="licenseType">License Type</Label>
            <select
              id="licenseType"
              value={settings.licensing?.licenseType || 'trial'}
              onChange={(e) => updateSetting('licensing', 'licenseType', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="trial">Trial</option>
              <option value="basic">Basic</option>
              <option value="professional">Professional</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
          <div>
            <Label htmlFor="expirationDate">Expiration Date</Label>
            <Input
              id="expirationDate"
              type="date"
              value={settings.licensing?.expirationDate || ''}
              onChange={(e) => updateSetting('licensing', 'expirationDate', e.target.value)}
            />
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="autoRenewal"
              checked={settings.licensing?.autoRenewal || false}
              onChange={(e) => updateSetting('licensing', 'autoRenewal', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <Label htmlFor="autoRenewal">Enable Auto-Renewal</Label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Database className="h-5 w-5 mr-2" />
            Plugin Marketplace
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="enableMarketplace"
              checked={settings.licensing?.enableMarketplace || true}
              onChange={(e) => updateSetting('licensing', 'enableMarketplace', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <Label htmlFor="enableMarketplace">Enable Plugin Marketplace</Label>
          </div>
          <div>
            <Label htmlFor="marketplaceUrl">Marketplace URL</Label>
            <Input
              id="marketplaceUrl"
              value={settings.licensing?.marketplaceUrl || 'https://marketplace.helpdesk.com'}
              onChange={(e) => updateSetting('licensing', 'marketplaceUrl', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return renderGeneralSettings();
      case 'security':
        return renderSecuritySettings();
      case 'notifications':
        return renderNotificationSettings();
      case 'integrations':
        return renderIntegrationSettings();
      case 'licensing':
        return renderLicensingSettings();
      default:
        return renderGeneralSettings();
    }
  };

  return (
    <div className="global-settings p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Global Settings</h1>
        <p className="text-gray-600">Configure platform-wide policies and configurations</p>
      </div>

      {/* Save Status */}
      {saveStatus && (
        <div className={`mb-6 p-4 rounded-lg flex items-center space-x-2 ${
          saveStatus === 'success' ? 'bg-green-50 text-green-800' :
          saveStatus === 'error' ? 'bg-red-50 text-red-800' :
          'bg-blue-50 text-blue-800'
        }`}>
          {saveStatus === 'success' && <CheckCircle className="h-5 w-5" />}
          {saveStatus === 'error' && <AlertTriangle className="h-5 w-5" />}
          {saveStatus === 'reset' && <Info className="h-5 w-5" />}
          <span>
            {saveStatus === 'success' && 'Settings saved successfully!'}
            {saveStatus === 'error' && 'Failed to save settings. Please try again.'}
            {saveStatus === 'reset' && 'Settings reset to default values.'}
          </span>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex space-x-2">
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
          <Button variant="outline" onClick={handleReset}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset to Default
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {renderTabContent()}
    </div>
  );
};

export default GlobalSettings;
